import faqData from "../data/faqData.json";
import { COMPETITION_YEAR } from "../config/competition";

interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
}

interface OpenRouterResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
}

class AIChatService {
  private apiKey: string;
  private apiModel: string;
  private apiUrl: string;
  private faqContext: string;

  constructor() {
    this.apiModel =
      process.env.REACT_APP_OPENROUTER_MODEL || "google/gemini-2.5-flash-lite";
    this.apiKey = process.env.REACT_APP_OPENROUTER_API_KEY || "";
    this.apiUrl =
      process.env.REACT_APP_OPENROUTER_API_URL ||
      "https://openrouter.ai/api/v1";
    this.faqContext = this.buildFAQContext();
  }

  private buildFAQContext(): string {
    let context =
      `You are a helpful AI assistant for Vietnam Young Lions ${COMPETITION_YEAR} competition. Respond based on this FAQ information:\\n\\n`;

    Object.entries(faqData).forEach(([category, data]) => {
      context += `## ${category}\\n`;

      // Add English questions
      if (data.en && data.en.length > 0) {
        context += "**English:**\\n";
        data.en.forEach((item, index) => {
          context += `Q${index + 1}: ${item.question}\\n`;
          context += `A${index + 1}: ${item.answer}\\n\\n`;
        });
      }

      // Add Vietnamese questions
      if (data.vn && data.vn.length > 0) {
        context += "**Vietnamese:**\\n";
        data.vn.forEach((item, index) => {
          context += `Q${index + 1}: ${item.question}\\n`;
          context += `A${index + 1}: ${item.answer}\\n\\n`;
        });
      }

      context += "\\n";
    });

    context += `
STRICT INSTRUCTIONS:
- ONLY answer questions about Vietnam Young Lions ${COMPETITION_YEAR} competition using the FAQ above
- REFUSE to discuss politics, life advice, economics, general topics, or anything unrelated to the competition
- Match user language: Vietnamese questions → Vietnamese answers, English questions → English answers
- If asked about non-competition topics, politely redirect: "I can only help with Vietnam Young Lions ${COMPETITION_YEAR} competition questions. Please ask about registration, categories, rules, or prizes."
- If unsure about competition details, suggest: "For more details, contact competition@aimacademy.vn"
- Stay professional and focused on competition topics only
- Competition topics include: registration, eligibility, categories (Film/Digital/Marketers), leagues, fees, rules, prizes, language requirements, Creative Camp (for finalists)
`;

    return context;
  }

  private isCompetitionRelated(message: string): boolean {
    // Only block obviously off-topic questions
    const offTopicKeywords = [
      // Không dùng 'movie'/'phim' (trùng hạng mục Film) và 'yêu' (khớp nhầm "yêu cầu")
      'weather', 'food', 'cooking', 'recipe', 'sports', 'football', 'music', 'dating',
      'politics', 'political', 'government', 'president', 'election', 'economy', 'stock', 'investment',
      'health', 'medicine', 'doctor', 'hospital', 'disease', 'travel', 'vacation', 'shopping',
      'thời tiết', 'món ăn', 'nấu ăn', 'thể thao', 'bóng đá', 'tình yêu', 'hẹn hò',
      'chính trị', 'tổng thống', 'bầu cử', 'kinh tế', 'chứng khoán', 'đầu tư', 'sức khỏe', 'bác sĩ', 'bệnh viện'
    ];

    const lowerMessage = message.toLowerCase();
    
    // If message contains obvious off-topic keywords, block it
    const isOffTopic = offTopicKeywords.some(keyword => lowerMessage.includes(keyword));
    
    // Allow all questions that aren't obviously off-topic
    // Let the AI's system prompt handle the filtering instead
    return !isOffTopic;
  }

  async sendMessage(
    userMessage: string,
    conversationHistory: ChatMessage[] = []
  ): Promise<string> {
    if (!this.apiKey) {
      return "Please configure your OpenRouter API key in .env.local file to enable AI chat functionality.";
    }

    // Pre-filter: Check if question is competition-related
    if (!this.isCompetitionRelated(userMessage)) {
      // Detect language to respond appropriately
      const isVietnamese =
        /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i.test(
          userMessage
        );

      if (isVietnamese) {
        return `Tôi chỉ có thể hỗ trợ các câu hỏi về cuộc thi Vietnam Young Lions ${COMPETITION_YEAR}. Vui lòng hỏi về đăng ký, hạng mục thi, luật thi, hoặc giải thưởng nhé!`;
      } else {
        return `I can only help with Vietnam Young Lions ${COMPETITION_YEAR} competition questions. Please ask about registration, categories, rules, or prizes!`;
      }
    }

    try {
      // Limit conversation history to last 6 messages for free tier
      const recentHistory = conversationHistory.slice(-6);

      const messages: ChatMessage[] = [
        {
          role: "system",
          content: this.faqContext,
        },
        ...recentHistory,
        {
          role: "user",
          content: userMessage,
        },
      ];

      const response = await fetch(`${this.apiUrl}/chat/completions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
          "Content-Type": "application/json",
          "HTTP-Referer": window.location.origin,
          "X-Title": `Vietnam Young Lions ${COMPETITION_YEAR} FAQ Chat`,
        },
        body: JSON.stringify({
          model: this.apiModel,
          messages: messages,
          max_tokens: 400, // Optimized for free tier
          temperature: 0.2, // Lower temperature for more focused responses
          top_p: 0.8, // More focused responses
          stream: false,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("OpenRouter Error:", response.status, errorText);

        if (response.status === 429) {
          return "I'm currently busy. Please wait a moment and try again.";
        }
        if (response.status === 401) {
          return "API key configuration error. Please check your OpenRouter setup.";
        }
        throw new Error(`API error: ${response.status}`);
      }

      const data: OpenRouterResponse = await response.json();

      if (
        data.choices &&
        data.choices.length > 0 &&
        data.choices[0].message?.content
      ) {
        const response = data.choices[0].message.content.trim();

        // Light post-filter: Only block extremely off-topic responses
        const extremeOffTopicPhrases = [
          "stock market", "cryptocurrency", "bitcoin", "investment portfolio", "medical diagnosis", 
          "relationship advice", "dating tips", "cooking recipe", "weather forecast",
          "chứng khoán", "tiền điện tử", "bitcoin", "chẩn đoán y tế", "tư vấn tình cảm", "công thức nấu ăn"
        ];

        const isExtremelyOffTopic = extremeOffTopicPhrases.some((phrase) =>
          response.toLowerCase().includes(phrase.toLowerCase())
        );

        if (isExtremelyOffTopic) {
          const isVietnamese =
            /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i.test(
              userMessage
            );

          if (isVietnamese) {
            return `Tôi chỉ có thể hỗ trợ các câu hỏi về cuộc thi Vietnam Young Lions ${COMPETITION_YEAR}. Vui lòng hỏi về đăng ký, hạng mục thi, luật thi, hoặc giải thưởng!`;
          } else {
            return `I can only help with Vietnam Young Lions ${COMPETITION_YEAR} competition questions. Please ask about registration, categories, rules, or prizes!`;
          }
        }

        return response;
      }

      return "I apologize, but I couldn't generate a proper response. Please rephrase your question.";
    } catch (error) {
      console.error("AI Chat Service Error:", error);

      // Provide fallback search functionality
      const searchResults = this.searchFAQ(userMessage);
      if (searchResults.length > 0) {
        const result = searchResults[0];
        return `Based on our FAQ: ${result.answer}\\n\\nFor more information, contact competition@aimacademy.vn`;
      }

      return "Sorry, I'm having trouble connecting right now. Please try again or contact competition@aimacademy.vn for assistance.";
    }
  }

  // Search for relevant FAQ items based on user query
  searchFAQ(query: string): Array<{
    question: string;
    answer: string;
    category: string;
    language: string;
  }> {
    const results: Array<{
      question: string;
      answer: string;
      category: string;
      language: string;
    }> = [];
    const searchTerm = query.toLowerCase();

    Object.entries(faqData).forEach(([category, data]) => {
      // Search English FAQs
      data.en.forEach((item) => {
        if (
          item.question.toLowerCase().includes(searchTerm) ||
          item.answer.toLowerCase().includes(searchTerm)
        ) {
          results.push({
            question: item.question,
            answer: item.answer,
            category: category,
            language: "en",
          });
        }
      });

      // Search Vietnamese FAQs
      data.vn.forEach((item) => {
        if (
          item.question.toLowerCase().includes(searchTerm) ||
          item.answer.toLowerCase().includes(searchTerm)
        ) {
          results.push({
            question: item.question,
            answer: item.answer,
            category: category,
            language: "vn",
          });
        }
      });
    });

    return results.slice(0, 5); // Return top 5 results
  }
}

export default new AIChatService();
export type { ChatMessage };
